import React, { useRef, useEffect, useState } from 'react';

// Helper for random float in range
function random(min: number, max: number) {
  return Math.random() * (max - min) + min;
}

// Ember type
interface Ember {
  id: number;
  x: number;
  y: number;
  size: number;
  opacity: number;
  speed: number;
  drift: number;
}

interface Spark {
  id: number;
  x: number;
  y: number;
  size: number;
  opacity: number;
  speed: number;
  drift: number;
  angle: number;
}

const FIRE_WIDTH = 140;
const FIRE_HEIGHT = 180;
const EMBER_SPAWN_INTERVAL = 500; // ms
const MAX_EMBERS = 3;
const MAX_SPARKS = 15;
const SPARK_SPAWN_INTERVAL = 100; // ms

// Ember spawn positions: right, left, middle
const EMBER_POSITIONS = [
  { x: 160, y: 100 }, // right
  { x: 96, y: 90 },  // left
  { x: 128, y: 60 },  // middle
];

// Spark spawn positions: behind the flame peaks
const SPARK_POSITIONS = [
  { x: 160, y: 60, angle: -45 },   // behind right peak
  { x: 96, y: 60, angle: 45 },    // behind left peak
  { x: 155, y: 70, angle: -30 },   // behind right peak lower
  { x: 101, y: 70, angle: 30 },    // behind left peak lower
  { x: 165, y: 65, angle: -40 },   // behind right peak upper
  { x: 150, y: 75, angle: -35 },   // behind right peak middle
  { x: 158, y: 55, angle: -50 },   // behind right peak top
  { x: 170, y: 50, angle: -55 },   // right peak top right
  { x: 145, y: 45, angle: -60 },   // right peak top left
  { x: 175, y: 70, angle: -35 },   // right peak far right
  { x: 140, y: 80, angle: -25 },   // right peak far left
  { x: 86, y: 50, angle: 55 },    // left peak top left
  { x: 111, y: 45, angle: 60 },   // left peak top right
  { x: 81, y: 70, angle: 35 },    // left peak far left
  { x: 116, y: 80, angle: 25 },   // left peak far right
];

// Helper to generate the main flame path with animated peaks
function getFlamePath(peak1: number, peak2: number, peak1X: number, peak2X: number) {
  // peak1: left peak height, peak2: right peak height
  // peak1X: left peak horizontal offset, peak2X: right peak horizontal offset
  return `M${143.38 + peak2X},${17.85 + peak2} 
    a8,8,0,0,0-12.63,3.41l-22,60.41
    L${84.59 + peak1X},${58.26 + peak1}
    a8,8,0,0,0-11.93.89C51,87.53,40,116.08,40,144
    a88,88,0,0,0,176,0C216,84.55,165.21,36,${143.38 + peak2X},${17.85 + peak2}Z`;
}

const FireAnimation: React.FC = () => {
  const [embers, setEmbers] = useState<Ember[]>([]);
  const [sparks, setSparks] = useState<Spark[]>([]);
  const [peak1, setPeak1] = useState(0); // left peak vertical
  const [peak2, setPeak2] = useState(0); // right peak vertical
  const [peak1X, setPeak1X] = useState(0); // left peak horizontal
  const [peak2X, setPeak2X] = useState(0); // right peak horizontal
  const [flameX, setFlameX] = useState(0); // overall horizontal movement
  const emberId = useRef(0);
  const sparkId = useRef(0);
  const animationRef = useRef<number>();
  const spawnIndex = useRef(0);
  const sparkSpawnIndex = useRef(0);

  // Animate fire flicker and peaks
  useEffect(() => {
    let running = true;
    function animate() {
      const time = Date.now();
      // Create vertical S-wave pattern
      // Left peak
      setPeak1(
        Math.sin(time / 300) * 6 + // Primary vertical movement (faster)
        Math.sin(time / 150) * 3   // Secondary wave for S curve (faster)
      );
      setPeak1X(
        Math.sin(time / 300) * 5 + // Increased horizontal movement (faster)
        Math.cos(time / 150) * 3   // Additional horizontal detail (faster)
      );
      
      // Right peak (increased phase offset for more separation)
      setPeak2(
        Math.sin((time + 300) / 300) * 6 + // Primary vertical movement (faster)
        Math.sin((time + 150) / 150) * 3   // Secondary wave for S curve (faster)
      );
      setPeak2X(
        Math.sin((time + 300) / 300) * 5 + // Increased horizontal movement (faster)
        Math.cos((time + 150) / 150) * 3   // Additional horizontal detail (faster)
      );
      
      // Very subtle overall movement
      setFlameX(Math.sin(time / 1000) * 0.5);
      if (running) animationRef.current = requestAnimationFrame(animate);
    }
    animate();
    return () => {
      running = false;
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  // Animate embers
  useEffect(() => {
    let running = true;
    function updateEmbers() {
      setEmbers((prev) =>
        prev
          .map((ember) => ({
            ...ember,
            y: ember.y - ember.speed,
            x: ember.x + ember.drift,
            opacity: ember.opacity - 0.01,
          }))
          .filter((ember) => ember.opacity > 0 && ember.y > -100)
      );
      if (running) animationRef.current = requestAnimationFrame(updateEmbers);
    }
    updateEmbers();
    return () => {
      running = false;
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  // Spawn embers at intervals
  useEffect(() => {
    const interval = setInterval(() => {
      setEmbers((prev) => {
        if (prev.length >= MAX_EMBERS) return prev;
        // Cycle through right, left, middle positions
        const pos = EMBER_POSITIONS[spawnIndex.current];
        spawnIndex.current = (spawnIndex.current + 1) % EMBER_POSITIONS.length;
        // Ember size and other properties remain random for variety
        const size = random(14, 22);
        return [
          ...prev,
          {
            id: emberId.current++,
            x: pos.x,
            y: pos.y,
            size,
            opacity: 1,
            speed: random(0.5, 1.0),
            drift: random(-0.5, 0.8),
          },
        ];
      });
    }, EMBER_SPAWN_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  // Spawn sparks at intervals
  useEffect(() => {
    const interval = setInterval(() => {
      setSparks((prev) => {
        if (prev.length >= MAX_SPARKS) return prev;
        const pos = SPARK_POSITIONS[sparkSpawnIndex.current];
        sparkSpawnIndex.current = (sparkSpawnIndex.current + 1) % SPARK_POSITIONS.length;
        
        const isRightSide = pos.x > 128;
        const baseDrift = isRightSide ? 1.5 : -1.5;
        
        return [
          ...prev,
          {
            id: sparkId.current++,
            x: pos.x,
            y: pos.y,
            size: random(3, 5),
            opacity: 0.9,
            speed: random(0.8, 1.5), // Reduced speed range
            drift: baseDrift + random(-0.3, 0.3),
            angle: pos.angle + random(-10, 10),
          },
        ];
      });
    }, SPARK_SPAWN_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  // Animate sparks
  useEffect(() => {
    let running = true;
    function updateSparks() {
      setSparks((prev) =>
        prev
          .map((spark) => ({
            ...spark,
            y: spark.y - spark.speed,
            x: spark.x + spark.drift,
            opacity: spark.opacity - 0.03, // Faster fade rate
            angle: spark.angle + random(-5, 5),
          }))
          .filter((spark) => spark.opacity > 0 && spark.y > -100)
      );
      if (running) animationRef.current = requestAnimationFrame(updateSparks);
    }
    updateSparks();
    return () => {
      running = false;
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  // Fire flicker transform (debug: only translateX)
  const flickerTransform = `translateX(${flameX}px)`;

  return (
    <svg
      width={FIRE_WIDTH}
      height={FIRE_HEIGHT}
      viewBox="0 0 256 256"
      style={{ display: 'block', margin: '0 auto', userSelect: 'none' }}
    >
      {/* Sparks (rendered first so they appear behind the flame) */}
      {sparks.map((spark) => (
        <g
          key={spark.id}
          transform={`translate(${spark.x}, ${spark.y}) rotate(${spark.angle})`}
        >
          <line
            x1={-spark.size}
            y1={0}
            x2={spark.size}
            y2={0}
            stroke="var(--color-yellow)"
            strokeWidth={2} // Increased stroke width
            opacity={spark.opacity}
          />
          <line
            x1={0}
            y1={-spark.size}
            x2={0}
            y2={spark.size}
            stroke="var(--color-yellow)"
            strokeWidth={2} // Increased stroke width
            opacity={spark.opacity}
          />
        </g>
      ))}

      {/* Main flame */}
      <g style={{ transform: flickerTransform, transformOrigin: '128px 180px' }}>
        <path
          d={getFlamePath(peak1, peak2, peak1X, peak2X)}
          fill="var(--color-orange)"
        />
        {/* Inner flame, lighter orange/yellow */}
        <ellipse
          cx="128"
          cy="170"
          rx="38"
          ry="48"
          fill="var(--color-yellow)"
        />
        {/* Core, light yellow orange */}
        <ellipse
          cx="128"
          cy="190"
          rx="18"
          ry="22"
          fill="#FFE066"
        />
        {/* Highlight for extra glow */}
        <ellipse
          cx="128"
          cy="180"
          rx="10"
          ry="16"
          fill="white"
          opacity="0.2"
        />
      </g>

      {/* Embers */}
      {embers.map((ember) => (
        <ellipse
          key={ember.id}
          cx={ember.x}
          cy={ember.y}
          rx={ember.size * 0.7}
          ry={ember.size}
          fill="var(--color-orange)"
          opacity={ember.opacity}
        />
      ))}
    </svg>
  );
};

export default FireAnimation;
